"use client";

import { useEffect } from "react";
import { AlertTriangle } from "lucide-react";
import { Button } from "../ui";
import { trackError } from "../../lib/error-tracking";

interface Props {
  error: Error & { digest?: string };
  /**
   * Rendert die Kinder neu, **ohne** die Inhalte neu zu laden.
   *
   * Optional, weil Next sie kuenftig nicht mehr zwingend reicht — und weil ein
   * Pflichtfeld hier jeden Aufrufer braeche, der nur `unstable_retry` bekommt.
   */
  reset?: () => void;
  /**
   * Next 16.2.0+: laedt die Inhalte der Fehlergrenze NEU und rendert sie neu.
   *
   * Das ist der Knopf, den der Nutzer meint. `reset` allein rendert mit
   * DENSELBEN gescheiterten Daten neu — bei einem Fehler aus einem Serverabruf
   * (der haeufigste Fall hier) laeuft es sofort wieder in denselben Fehler, und
   * „Erneut versuchen" tut sichtbar nichts. Nextens eigene Doku sagt es so:
   * „In most cases, you should use `unstable_retry()` instead."
   * (`next/dist/docs/01-app/03-api-reference/03-file-conventions/error.md`,
   * Abschnitt `reset`; die Eigenschaft kam in 16.2.0 dazu, wir fahren 16.2.4.)
   */
  unstable_retry?: () => void;
}

/**
 * Die EINE Fehlerseite der Suite — fuer `app/error.tsx` wie fuer
 * `app/(app)/error.tsx`. `AppError`, `AppErrorTracked` und `RootErrorTracked`
 * sind seit 2026-08-19 nur noch Namen fuer diese Fassung.
 *
 * Vorher waren es drei Fassungen im selben Slot, die sich in allem Sichtbaren
 * unterschieden — und nur zwei von ihnen meldeten den Fehler ueberhaupt an den
 * `errorTracker`. Fuenf Apps (admin, ai-chat, auth, automations, hub) buchten
 * ihre Fehler nirgends: sie schrieben in die Browser-Konsole und sonst nirgendwo,
 * der Ringpuffer, den `errorTracker.getErrors()` ausliest, blieb leer.
 *
 * `h-full min-h-[60vh]` statt `min-h-screen` — beide Teile tragen etwas:
 *
 * - `min-h-screen` ist FALSCH, sobald die Seite im Inhaltsbereich steht. Das
 *   `<main>` in `AppLayout.tsx` ist `flex-1 overflow-y-auto` und damit bereits
 *   kuerzer als der Bildschirm; 100vh darin erzwingen einen zweiten Scrollbalken
 *   neben dem der Seite.
 * - `h-full` allein reicht aber nicht. Ueber `app/error.tsx` faellt die Seite aus
 *   dem `(app)`-Layout heraus und haengt direkt am `<body>` — und der traegt je
 *   nach App `h-[100dvh]` (dann greift `h-full`) ODER nur `min-h-screen`
 *   (hub, admin, ai-chat, auth). Bei `min-height` ist die Elternhoehe `auto`,
 *   `height: 100%` faellt auf `auto` zurueck, und der Inhalt klebt oben statt in
 *   der Mitte. `min-h-[60vh]` faengt genau diesen Fall — und bleibt klein genug,
 *   um im `<main>` (dort rund 90vh hoch) keinen Scrollbalken auszuloesen.
 */
export default function ErrorPage({ error, reset, unstable_retry }: Props) {
  useEffect(() => {
    // BEIDES, nicht eines von beidem. Die zwei Vorgaenger protokollierten
    // gegenlaeufig, und wer nur einen von ihnen fortschreibt, nimmt der anderen
    // Haelfte der Apps etwas weg:
    //   `trackError` legt den Fehler in den Ringpuffer (`errorTracker.getErrors()`),
    //   schreibt aber NUR unter `NODE_ENV=development` in die Konsole —
    //   in Produktion also nirgendwohin, das jemand sieht (und den Puffer liest
    //   Stand 2026-08-19 keine App aus).
    //   Die alte `AppError` protokollierte umgekehrt IMMER und buchte nirgends.
    trackError(error, { component: "ErrorBoundary" });
    console.error("[App Error]", error);
  }, [error]);

  const erneut = unstable_retry ?? reset;

  return (
    <div className="flex h-full min-h-[60vh] flex-col items-center justify-center gap-4 p-8">
      <AlertTriangle size={32} className="text-destructive" aria-hidden="true" />
      <h2 className="text-lg font-semibold">Etwas ist schiefgelaufen</h2>
      <p className="text-sm text-muted-foreground max-w-md text-center">
        {error.message || "Ein unerwarteter Fehler ist aufgetreten."}
      </p>
      {error.digest && (
        <p className="text-xs text-muted-foreground/70 font-mono">Fehler-ID: {error.digest}</p>
      )}
      {/* `unstable_retry` zuerst, `reset` als Rueckfall — der Knopf haengt damit
          nicht an einer als `unstable_` gekennzeichneten Schnittstelle allein.
          Faellt beides weg, wird er gar nicht erst angeboten: ein Knopf, der
          nichts tut, ist schlechter als keiner. (Dasselbe Muster fuehrt
          `janeway-messenger/app/global-error.tsx` seit 2026-09.) */}
      {erneut && (
        <Button onClick={erneut} aria-label="Erneut versuchen">Erneut versuchen</Button>
      )}
    </div>
  );
}
